import { differenceInSeconds } from "date-fns";
import { Dispatch, SetStateAction, useEffect, useLayoutEffect, useRef } from "react";
import { useLocation } from "react-router";
import {
  InlineModal,
  StyledIframe
} from "./ScormWrapper.styles";
import { useNavigate } from "../../services";
import { useAuthStore, useLanguageStore, useNavbarStore } from "../../stores";
import type { LearningActivity, Level } from "../../types";
import { getPreviousRoute } from "../../utils/general";

export function ScormWrapper({
  activityId,
  close,
  courseId,
  // fullScreen=false,
  isOpen,
  learningObjectTypology,
  learningObjectStarted,
  pathId,
  canAccessNow,
  canAccessUntil,
  setTriggerPause
} : {
  activityId: number
  close?: () => void
  courseId: string | null
  // fullScreen?: boolean
  isOpen: boolean
  learningObjectTypology: Level
  learningObjectStarted?: LearningActivity
  pathId: string | null,
  // aggiunti con il refactoring autoplay
  canAccessNow?: boolean,
  canAccessUntil?: string,
  setTriggerPause?:Dispatch<SetStateAction<boolean>>
}) {

  const modalRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const uri = `${window.location.origin}/esplora/scorm/${activityId}/${learningObjectTypology}`;
  const language = useLanguageStore(state => state.language)?.id;

  // il passaggio dei dati tramite questa postMessage mandava status stale E:
  // - Il CTA in dettaglio legge P (giusto).
  // - In watch hai un valore iniziale stale E (cache + refetch in corso).
  // - Quel valore stale viene serializzato e inviato via postMessage.
  // - Nel frame SCORM il dato da postMessage ha priorità e blocca il fetch locale (di fatto non corregge più a P).
  // è stato quindi rimossa la chiamata postMessage per DA_SCORM 2026-05-19
  const shouldUsePostMessageBootstrap = Boolean(learningObjectStarted)
    && learningObjectTypology !== "DA_SCORM";
  const params = `courseId=${courseId || null}&rootId=${pathId || null}&lang=${language}`;
  const navbarItem = useNavbarStore(state => state.item);
  const { pathname }=useLocation();
  const navigate = useNavigate();

  useLayoutEffect(() => {
    if (isOpen) {
      iframeRef.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      if (modalRef.current && !document.contains(modalRef.current)) {
        observer.disconnect();
        const previousPath =
          getPreviousRoute({
            courseId: (courseId ? Number(courseId) : undefined),
            id: activityId,
            learningObjectTypology,
            path: pathname,
            rootId: (pathId ? Number(pathId) : undefined),
            section: navbarItem
          });
        navigate(previousPath);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    return () => observer.disconnect();
  }, [activityId, learningObjectTypology, courseId, pathId, pathname, navbarItem, navigate]);


  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (event.data.type === "SCORM_READY") {
        if (
          iframeRef.current
          && iframeRef.current.contentWindow
          && learningObjectStarted
          && shouldUsePostMessageBootstrap
        ) {
          iframeRef.current.contentWindow.postMessage({
            data: learningObjectStarted,
            type: "START_OR_RESUME_DATA"
          }, window.location.origin);
        }
      }
    }
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
    };
  }, [learningObjectStarted, shouldUsePostMessageBootstrap]);

  // Refresh proattivo del token mentre la lightbox e' aperta. Gli hook sono
  // esposti qui (main window) e non nell'iframe /esplora/scorm, che viene
  // rimpiazzato da scorm_player.html: la libreria li legge via window.top.
  useEffect(() => {
    if (!isOpen) return;

    const getToken = () =>
      useAuthStore.getState().session?.getAccessToken().getJwtToken() ?? "";
    const getSession = () =>
      useAuthStore.getState().sessionToken ?? "";

    window.olGetToken = getToken;
    window.olGetSessionToken = getSession;
    window.olRefreshToken = async () => {
      const state = useAuthStore.getState();
      if (state.session && state.sessionToken) {
        await state.refreshSession();
      }
      return getToken();
    };

    let timer: ReturnType<typeof setTimeout>;
    const REFRESH_MARGIN_MS = 60_000; // rinnova ~1 min prima della scadenza
    const MIN_DELAY_MS = 5_000;

    const scheduleRefresh = () => {
      const session = useAuthStore.getState().session;
      if (!session) return;
      const expMs = session.getAccessToken().getExpiration() * 1000;
      const delay = Math.max(expMs - Date.now() - REFRESH_MARGIN_MS, MIN_DELAY_MS);
      timer = setTimeout(async () => {
        try {
          await window.olRefreshToken?.();
        } catch {
          // refreshSession gestisce gia' il signOut in caso di errore
        }
        scheduleRefresh();
      }, delay);
    };
    scheduleRefresh();

    return () => {
      clearTimeout(timer);
      delete window.olGetToken;
      delete window.olGetSessionToken;
      delete window.olRefreshToken;
    };
  }, [isOpen]);

  const handleIframeLoad = () => {
    // We now primarily rely on SCORM_READY, but we can keep a small delay as fallback
    // if we haven't received SCORM_READY yet.
    // However, the handshake is much safer for non-Chrome browsers.
  };

  //TODO manca il tracciamento alla chiusura dello SCORM su blocco orario
  //_______Logica blocco orario SCORM__________START
  const timeUpdateHandlerScorm = () => {
    if(canAccessNow && canAccessUntil){
      const timeLeft = differenceInSeconds(new Date(canAccessUntil), new Date());
      if(timeLeft <= 0){
        close && close();
        setTriggerPause && setTriggerPause(true);
      }
    }
  };
  //timeUpdateHandlerScorm viene eseguita ogni 250 millisecondi e se il range orarario in cui l'utente può accedere allo
  //scorm è > di new Date verrà mostrato il pop up <LearningActivityBlockOverlay>

  useEffect(() => {

    if (!isOpen || learningObjectTypology !== "DA_SCORM"|| !canAccessNow || !canAccessUntil) return;
    const intervalId = setInterval(() => {
      timeUpdateHandlerScorm();
    }, 250);
    return () => clearInterval(intervalId);
  }, [isOpen,learningObjectTypology,canAccessNow,canAccessUntil]);
  //_______Logica blocco orario SCORM__________END


  useEffect(() => {
    if (!isOpen || !modalRef.current) return;

    const toRestore: HTMLElement[] = [];
    let node: HTMLElement | null = modalRef.current;

    while (node && node !== document.body) {
      const parent: HTMLElement | null = node.parentElement;
      if (parent) {
        Array.from(parent.children).forEach((sibling) => {
          const el = sibling as HTMLElement;
          if (el !== node && !el.hasAttribute("inert")) {
            el.setAttribute("inert", "");
            toRestore.push(el);
          }
        });
      }
      node = parent;
    }

    return () => {
      toRestore.forEach(el => el.removeAttribute("inert"));
    };
  }, [isOpen]);

  return (
    <InlineModal $open={ isOpen } ref={ modalRef }>
      <StyledIframe
        ref={ iframeRef }
        src={ `${uri}?${params}${shouldUsePostMessageBootstrap ? "&hasPostMessageData=true" : ""}` }
        title={ `iframe-scorm-${learningObjectTypology}` }
        onLoad={ handleIframeLoad }
      />
    </InlineModal>
  );
}
